// Mobile League Stats — NBA.com-style sortable box-score table with Season / Season Type /
// Per Mode filters. Over ZenGM's playerStats view: statType (Totals/Per Game/Per 36) and
// playoffs map to native worker params (changing a filter navigates to the URL variant);
// sorting is client-side.

import { useState } from "react";
import useTitleBar from "../hooks/useTitleBar.tsx";
import { useLocal } from "../util/local.ts";
import { realtimeUpdate } from "../util/realtimeUpdate.ts";
import { helpers } from "../util/helpers.ts";
import { getCols } from "../../common/getCols.ts";
import type { View } from "../../common/types.ts";

const MODES: [string, string][] = [
	["totals", "Totals"],
	["perGame", "Per Game"],
	["per36", "Per 36"],
];
const TYPES: [string, string][] = [
	["regularSeason", "Regular Season"],
	["playoffs", "Playoffs"],
];

const GmStats = ({
	players,
	stats,
	statType,
	playoffs,
	season,
}: View<"playerStats">) => {
	useTitleBar({ title: "League Stats" });
	const {
		lid,
		teamInfoCache,
		userTid,
		season: currentSeason,
		startingSeason,
	} = useLocal(["lid", "teamInfoCache", "userTid", "season", "startingSeason"]);

	const [sortKey, setSortKey] = useState("pts");
	const [dir, setDir] = useState(-1);
	const [menu, setMenu] = useState<string | null>(null);
	// Default the individual team filter to the user's own team (keyed by abbrev, like teamOpts).
	const [teamFilter, setTeamFilter] = useState(
		(teamInfoCache as any[])[userTid]?.abbrev ?? "all",
	);
	const [posFilter, setPosFilter] = useState("all");

	const cols = getCols((stats as string[]).map((s) => `stat:${s}`));

	// Anchor the year picker to the LEAGUE's current season (not the viewed one — anchoring to
	// the viewed season made past years shift the options and strand the user), spanning back to
	// startingSeason (1987 in leagues created with real historical stats).
	const seasonNum = typeof season === "number" ? season : currentSeason;
	const latestSeason = currentSeason ?? seasonNum;
	const earliestSeason = Math.min(startingSeason ?? latestSeason, latestSeason);
	const seasonOpts: number[] = [];
	for (let s = latestSeason; s >= earliestSeason; s--) {
		seasonOpts.push(s);
	}

	const teamOpts = [
		{ key: "all", label: "All teams", active: teamFilter === "all" },
		...(teamInfoCache as any[])
			.filter((t) => t && t.abbrev)
			.sort((a, b) => (a.region || "").localeCompare(b.region || ""))
			.map((t) => ({
				key: t.abbrev,
				label: `${t.region} ${t.name}`,
				active: teamFilter === t.abbrev,
			})),
	];
	const POSITIONS = ["all", "PG", "SG", "SF", "PF", "C"];
	const posOpts = POSITIONS.map((p) => ({
		key: p,
		label: p === "all" ? "All positions" : p,
		active: posFilter === p,
	}));

	const go = (s: number | string, st: string, po: string) => {
		setMenu(null);
		realtimeUpdate([], `/l/${lid}/player_stats/all/${s}/${st}/${po}`);
	};
	const goTeamStats = () =>
		realtimeUpdate([], `/l/${lid}/team_stats_m/${seasonNum}/${playoffs}`);

	const rows = [...(players as any[])]
		.filter((p) => teamFilter === "all" || p.abbrev === teamFilter)
		.filter((p) => posFilter === "all" || p.pos === posFilter)
		.sort((a, b) => dir * ((a.stats[sortKey] ?? 0) - (b.stats[sortKey] ?? 0)));

	const Pill = ({ id, label, options, onPick }: any) => (
		<div className="gms-pillwrap">
			<button
				className="gms-pill"
				onClick={() => setMenu(menu === id ? null : id)}
			>
				{label} ▾
			</button>
			{menu === id ? (
				<div className="gms-menu">
					{options.map((o: any) => (
						<button
							key={o.key}
							className="gms-menu-item"
							onClick={() => onPick(o.key)}
						>
							{o.active ? "✓ " : ""}
							{o.label}
						</button>
					))}
				</div>
			) : null}
		</div>
	);

	return (
		<div className="gms">
			<style>{styles}</style>
			<h1 className="gms-title">League Stats</h1>

			<div className="gms-seg">
				<button className="gms-seg-btn active">Individual</button>
				<button className="gms-seg-btn" onClick={goTeamStats}>
					Team
				</button>
			</div>

			<div className="gms-filters">
				<Pill
					id="season"
					label={`${seasonNum}`}
					options={seasonOpts.map((s) => ({
						key: s,
						label: `${s}`,
						active: s === seasonNum,
					}))}
					onPick={(s: number) => go(s, statType, playoffs)}
				/>
				<Pill
					id="type"
					label={TYPES.find(([k]) => k === playoffs)?.[1] ?? "Regular Season"}
					options={TYPES.map(([k, l]) => ({
						key: k,
						label: l,
						active: k === playoffs,
					}))}
					onPick={(po: string) => go(seasonNum, statType, po)}
				/>
				<Pill
					id="mode"
					label={MODES.find(([k]) => k === statType)?.[1] ?? "Per Game"}
					options={MODES.map(([k, l]) => ({
						key: k,
						label: l,
						active: k === statType,
					}))}
					onPick={(st: string) => go(seasonNum, st, playoffs)}
				/>
				<Pill
					id="team"
					label={teamFilter === "all" ? "All teams" : teamFilter}
					options={teamOpts}
					onPick={(k: string) => {
						setMenu(null);
						setTeamFilter(k);
					}}
				/>
				<Pill
					id="pos"
					label={posFilter === "all" ? "All pos" : posFilter}
					options={posOpts}
					onPick={(k: string) => {
						setMenu(null);
						setPosFilter(k);
					}}
				/>
			</div>

			<div className="gms-tablewrap">
				<table className="gms-table">
					<thead>
						<tr>
							<th className="gms-sticky gms-th">Player</th>
							{(stats as string[]).map((s, i) => (
								<th
									key={s}
									className={`gms-th ${sortKey === s ? "sorted" : ""}`}
									onClick={() => {
										if (sortKey === s) setDir(-dir);
										else {
											setSortKey(s);
											setDir(-1);
										}
									}}
								>
									{cols[i]?.title ?? s}
									{sortKey === s ? (dir === -1 ? " ▼" : " ▲") : ""}
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						{rows.map((p, r) => (
							<tr key={p.pid}>
								<td className="gms-sticky gms-name">
									<span className="gms-rank">{r + 1}</span>
									<span
										className="gms-namelink"
										onClick={() =>
											realtimeUpdate([], helpers.leagueUrl(["player", p.pid]))
										}
									>
										{p.firstNameShort ?? p.firstName} {p.lastName}
									</span>
									<span className="gms-pos">{p.pos}</span>
								</td>
								{(stats as string[]).map((s) => (
									<td key={s} className={sortKey === s ? "sorted" : ""}>
										{helpers.roundStat(p.stats[s], s)}
									</td>
								))}
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</div>
	);
};

const styles = `
.gms { max-width:100%; padding:16px 12px 40px; color:#fff; font-family:Montserrat,system-ui,sans-serif; }
.gms-title { font-weight:900; font-size:30px; margin:0 0 14px; letter-spacing:-.5px; padding:0 4px; }
.gms-seg { display:flex; gap:6px; background:#181818; border-radius:12px; padding:5px; margin:0 4px 12px; }
.gms-seg-btn { flex:1; border:none; background:none; color:#9fb0bc; font-weight:800; font-size:13px;
  padding:9px; border-radius:9px; cursor:pointer; font-family:inherit; }
.gms-seg-btn.active { background:#18E0A8; color:#0a0a0a; }
.gms-filters { display:flex; gap:8px; flex-wrap:wrap; margin-bottom:14px; padding:0 4px; position:relative; z-index:5; }
.gms-pillwrap { position:relative; }
.gms-pill { border:none; border-radius:16px; background:#181818; color:#cfe0ec; font-weight:700;
  font-size:12px; padding:8px 13px; cursor:pointer; font-family:inherit; white-space:nowrap; }
.gms-menu { position:absolute; top:100%; left:0; margin-top:6px; background:#1c1c1c; border:1px solid #2a2a2a;
  border-radius:10px; padding:5px; min-width:150px; max-height:280px; overflow-y:auto;
  box-shadow:0 12px 30px rgba(0,0,0,.5); z-index:10; }
.gms-menu-item { display:block; width:100%; text-align:left; background:none; border:none; color:#cfe0ec;
  font-size:13px; font-weight:600; padding:9px 10px; border-radius:7px; cursor:pointer; font-family:inherit; white-space:nowrap; }
.gms-menu-item:hover { background:#232323; }
.gms-tablewrap { overflow-x:auto; -webkit-overflow-scrolling:touch; }
.gms-table { border-collapse:collapse; font-size:12px; white-space:nowrap; }
.gms-th { color:#8fb3c9; font-weight:800; font-size:10px; letter-spacing:.4px; padding:8px 10px;
  text-align:right; cursor:pointer; border-bottom:1px solid #232323; user-select:none; }
.gms-th.sorted { color:#18E0A8; }
.gms-table td { padding:9px 10px; text-align:right; border-bottom:1px solid #181818; color:#e6edf2; }
.gms-table td.sorted { color:#18E0A8; font-weight:700; }
.gms-sticky { position:sticky; left:0; text-align:left !important; background:#121212; z-index:2; }
.gms-th.gms-sticky { z-index:3; }
.gms-name { font-weight:700; font-size:13px; min-width:150px; }
.gms-namelink { cursor:pointer; }
.gms-namelink:active { color:#18E0A8; }
.gms-rank { color:#7f8b93; font-size:11px; margin-right:8px; }
.gms-pos { color:#8fb3c9; font-size:10px; margin-left:6px; }
`;

export default GmStats;
